import React, { useState, useEffect, useRef } from "react";
import {
  Row,
  Col,
  Card,
  Space,
  Button,
  Statistic,
  Select,
  Modal,
  message,
} from "antd";
import {
  PlusOutlined,
  SyncOutlined,
  DownloadOutlined,
  ShoppingCartOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
} from "@ant-design/icons";
import { useRequisition } from "../../../hooks/finance/useRequisition";
import { useBudget } from "../../../hooks/finance/useBudget";
import RequisitionTable from "../../../components/finance/requisition/RequisitionTable";
import RequisitionDetailDrawer from "../../../components/finance/requisition/RequisitionDetailDrawer";
import CreateRequisitionModal from "../../../components/finance/requisition/CreateRequisitionModal";
import * as XLSX from "xlsx";
import dayjs from "dayjs";

const { Option } = Select;

const RequisitionManagement = () => {
  const {
    requisitions,
    loading,
    fetchRequisitions,
    createRequisition,
    submitRequisition,
    hodApprove,
    financeApprove,
    markFulfilled,
    cancelRequisition,
    deleteRequisition,
    getStatistics,
  } = useRequisition();

  const {
    departments,
    fetchDepartments,
    fetchActiveBudgetForDepartment,
    lineItemsLoading,
  } = useBudget();

  const [detailsDrawerVisible, setDetailsDrawerVisible] = useState(false);
  const [createModalVisible, setCreateModalVisible] = useState(false);
  const [selectedRequisition, setSelectedRequisition] = useState(null);
  const [creating, setCreating] = useState(false);
  const [activeBudgetForModal, setActiveBudgetForModal] = useState(null);
  const activeBudgetSelectionRef = useRef(0);

  const [filters, setFilters] = useState({
    departmentId: null,
    status: null,
    priority: null,
  });

  useEffect(() => {
    fetchDepartments();
    fetchRequisitions();
  }, []);

  useEffect(() => {
    fetchRequisitions(filters);
  }, [filters]);

  const handleViewDetails = (requisition) => {
    setSelectedRequisition(requisition);
    setDetailsDrawerVisible(true);
  };

  const handleSubmit = async (requisition) => {
    Modal.confirm({
      title: "Submit Requisition",
      content: `Submit requisition "${requisition.title}" for approval?`,
      okText: "Yes, Submit",
      okType: "primary",
      onOk: async () => {
        await submitRequisition(requisition.requisitionId);
      },
    });
  };

  const handleHodApprove = async (requisition, action) => {
    if (action === "approve") {
      Modal.confirm({
        title: "HoD Approve",
        content: `Approve requisition "${requisition.title}"?`,
        okText: "Approve",
        okType: "primary",
        onOk: async () => {
          await hodApprove(requisition.requisitionId, "approve");
        },
      });
    } else {
      Modal.confirm({
        title: "HoD Reject",
        content: `Reject requisition "${requisition.title}"?`,
        okText: "Reject",
        okType: "danger",
        onOk: async () => {
          await hodApprove(
            requisition.requisitionId,
            "reject",
            "Rejected by HoD",
          );
        },
      });
    }
  };

  const handleFinanceApprove = async (requisition, action) => {
    if (action === "approve") {
      Modal.confirm({
        title: "Finance Approve",
        content: `Approve requisition "${requisition.title}" for procurement?`,
        okText: "Approve",
        okType: "primary",
        onOk: async () => {
          await financeApprove(requisition.requisitionId, "approve");
        },
      });
    } else {
      Modal.confirm({
        title: "Finance Reject",
        content: `Reject requisition "${requisition.title}"?`,
        okText: "Reject",
        okType: "danger",
        onOk: async () => {
          await financeApprove(
            requisition.requisitionId,
            "reject",
            "Rejected by Finance",
          );
        },
      });
    }
  };

  const handleMarkFulfilled = async (requisition) => {
    Modal.confirm({
      title: "Mark as Fulfilled",
      content: `Mark requisition "${requisition.title}" as fulfilled?`,
      okText: "Yes, Fulfilled",
      okType: "primary",
      onOk: async () => {
        await markFulfilled(requisition.requisitionId);
      },
    });
  };

  const handleCancel = async (requisition) => {
    Modal.confirm({
      title: "Cancel Requisition",
      content: `Cancel requisition "${requisition.title}"?`,
      okText: "Yes, Cancel",
      okType: "danger",
      onOk: async () => {
        await cancelRequisition(requisition.requisitionId);
      },
    });
  };

  const handleDelete = async (requisition) => {
    Modal.confirm({
      title: "Delete Requisition",
      content: `Delete requisition "${requisition.title}"? This cannot be undone.`,
      okText: "Yes, Delete",
      okType: "danger",
      onOk: async () => {
        await deleteRequisition(requisition.requisitionId);
      },
    });
  };

  const handleFetchLineItems = async (departmentId) => {
    const requestId = ++activeBudgetSelectionRef.current;
    if (!departmentId) {
      setActiveBudgetForModal(null);
      return;
    }
    const budget = await fetchActiveBudgetForDepartment(departmentId);
    if (requestId === activeBudgetSelectionRef.current) {
      setActiveBudgetForModal(budget);
    }
  };

  const handleCreateRequisition = async (data, attachments) => {
    setCreating(true);
    try {
      const result = await createRequisition(data, attachments);
      if (result) {
        setCreateModalVisible(false);
        setActiveBudgetForModal(null);
        return true;
      }
      return false;
    } finally {
      setCreating(false);
    }
  };

  const handleCloseCreateModal = () => {
    activeBudgetSelectionRef.current += 1;
    setCreateModalVisible(false);
    setActiveBudgetForModal(null);
  };

  const stats = getStatistics();

  const formatCurrency = (amount) => {
    return parseFloat(amount || 0).toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  };

  const exportToExcel = () => {
    const exportData = requisitions.map((r) => ({
      "Requisition #": r.requisitionNumber,
      Title: r.title,
      Item: r.departmentName || "-",
      "Total Amount": parseFloat(r.totalAmount || 0),
      Priority: r.priority,
      Status: r.status,
      "Requested By": r.requestedByName || "-",
      "Needed By": r.neededByDate
        ? dayjs(r.neededByDate).format("DD MMM YYYY")
        : "-",
      Created: r.createdAt ? dayjs(r.createdAt).format("DD MMM YYYY") : "-",
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Requisitions");
    XLSX.writeFile(wb, `requisitions_${dayjs().format("YYYY-MM-DD")}.xlsx`);
    message.success("Export completed");
  };

  return (
    <>
      <Space direction="vertical" style={{ width: "100%" }} size="large">
        {/* Statistics Cards */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Requisitions"
                value={stats.total}
                valueStyle={{ color: "#1890ff" }}
                prefix={<ShoppingCartOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Pending Approval"
                value={stats.submitted + stats.hodApproved}
                valueStyle={{ color: "#faad14" }}
                prefix={<ClockCircleOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="In Procurement"
                value={stats.inProcurement + stats.financeApproved}
                valueStyle={{ color: "#722ed1" }}
                prefix={<ShoppingCartOutlined />}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Fulfilled"
                value={stats.fulfilled}
                valueStyle={{ color: "#52c41a" }}
                prefix={<CheckCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Financial Summary */}
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Card>
              <Statistic
                title="Total Requested Amount"
                value={formatCurrency(stats.totalAmount)}
                prefix="UGX"
                valueStyle={{ color: "#1890ff", fontSize: "20px" }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12}>
            <Card>
              <Statistic
                title="Rejected Requisitions"
                value={stats.rejected}
                valueStyle={{ color: "#ff4d4f", fontSize: "20px" }}
                prefix={<ExclamationCircleOutlined />}
              />
            </Card>
          </Col>
        </Row>

        {/* Main Content */}
        <Card
          title={
            <Space>
              <ShoppingCartOutlined />
              <span>Requisitions</span>
            </Space>
          }
        >
          <Space direction="vertical" style={{ width: "100%" }} size="large">
            {/* Action Buttons */}
            <Row gutter={[8, 8]}>
              <Col xs={24}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: "8px",
                    flexWrap: "wrap",
                  }}
                >
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setCreateModalVisible(true)}
                  >
                    New Requisition
                  </Button>
                  <Button
                    icon={<SyncOutlined />}
                    onClick={() => fetchRequisitions(filters)}
                    loading={loading}
                  >
                    Refresh
                  </Button>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={exportToExcel}
                    disabled={requisitions.length === 0}
                  >
                    Export
                  </Button>
                </div>
              </Col>
            </Row>

            {/* Filters */}
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={8}>
                <Select
                  placeholder="Filter by Item"
                  style={{ width: "100%" }}
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  value={filters.departmentId}
                  onChange={(value) =>
                    setFilters({ ...filters, departmentId: value })
                  }
                >
                  {departments.map((d) => (
                    <Option
                      key={d.departmentId}
                      value={d.departmentId}
                      label={`${d.name} ${d.code || ""} ${d.description || ""}`}
                    >
                      {d.name}
                    </Option>
                  ))}
                </Select>
              </Col>
              <Col xs={24} sm={8}>
                <Select
                  placeholder="Filter by Status"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.status}
                  onChange={(value) =>
                    setFilters({ ...filters, status: value })
                  }
                >
                  <Option value="Draft">Draft</Option>
                  <Option value="Submitted">Submitted</Option>
                  <Option value="HoD Approved">HoD Approved</Option>
                  <Option value="Finance Approved">Finance Approved</Option>
                  <Option value="Procurement">In Procurement</Option>
                  <Option value="Fulfilled">Fulfilled</Option>
                  <Option value="HoD Rejected">HoD Rejected</Option>
                  <Option value="Finance Rejected">Finance Rejected</Option>
                </Select>
              </Col>
              <Col xs={24} sm={8}>
                <Select
                  placeholder="Filter by Priority"
                  style={{ width: "100%" }}
                  allowClear
                  value={filters.priority}
                  onChange={(value) =>
                    setFilters({ ...filters, priority: value })
                  }
                >
                  <Option value="Low">Low</Option>
                  <Option value="Medium">Medium</Option>
                  <Option value="High">High</Option>
                  <Option value="Urgent">Urgent</Option>
                </Select>
              </Col>
            </Row>

            {/* Requisition Table */}
            <RequisitionTable
              requisitions={requisitions}
              loading={loading}
              onViewDetails={handleViewDetails}
              onSubmit={handleSubmit}
              onHodApprove={handleHodApprove}
              onFinanceApprove={handleFinanceApprove}
              onMarkFulfilled={handleMarkFulfilled}
              onCancel={handleCancel}
              onDelete={handleDelete}
            />
          </Space>
        </Card>
      </Space>

      {/* Detail Drawer */}
      <RequisitionDetailDrawer
        visible={detailsDrawerVisible}
        onClose={() => {
          setDetailsDrawerVisible(false);
          setSelectedRequisition(null);
        }}
        requisitionId={selectedRequisition?.requisitionId}
        onSubmit={handleSubmit}
        onHodApprove={handleHodApprove}
        onFinanceApprove={handleFinanceApprove}
        onMarkFulfilled={handleMarkFulfilled}
      />

      {/* Create Modal */}
      <CreateRequisitionModal
        visible={createModalVisible}
        onCancel={handleCloseCreateModal}
        onSubmit={handleCreateRequisition}
        departments={departments}
        loading={creating}
        activeBudget={activeBudgetForModal}
        lineItemsLoading={lineItemsLoading}
        onFetchLineItems={handleFetchLineItems}
      />
    </>
  );
};

export default RequisitionManagement;
